import { Button, EnrollmentHelpText } from '@/shared/components'
import { StepHeader } from '../StepHeader'
import { TrainingDateSidebar } from './trainingDate/TrainingDateSidebar'
import { TrainingStartDateSection } from './trainingDate/TrainingStartDateSection'
import { useTrainingDateStep } from './trainingDate/useTrainingDateStep'
import { YardDaySelector } from './trainingDate/YardDaySelector'
import type { EnrollmentFieldEditability } from '../../utils/enrollmentHydration'

type TrainingDateStepProps = {
  currentStep: number
  totalSteps: number
  fieldEditability: EnrollmentFieldEditability
  trainingStartDate: string
  trainingMode: string | null | undefined
  onBack: () => void
  onContinue: () => void
  onTrainingStartDateChange: (value: string) => void
  yardDays: string[]
  onYardDaysChange: (days: string[]) => void
  isSubmitting?: boolean
  errorMessage?: string | null
  locationExternalId?: string | null
}

export function TrainingDateStep({
  currentStep,
  totalSteps,
  fieldEditability,
  trainingStartDate,
  trainingMode,
  onBack,
  onContinue,
  onTrainingStartDateChange,
  yardDays,
  onYardDaysChange,
  isSubmitting = false,
  errorMessage,
  locationExternalId
}: TrainingDateStepProps) {
  const isTrainingStartDateDisabled = fieldEditability.preferredStartDate === false
  const areYardDaysDisabled = fieldEditability.yardDays === false

  const {
    allowedDays,
    availableStartDates,
    calendarMaxDate,
    calendarMinDate,
    classroomSchedule,
    handleToggleYardDay,
    hasRequiredYardDays,
    isClassroomOnly,
    isContinueDisabled,
    isYardOnly,
    isStartDateAvailabilityError,
    isStartDateAvailabilityUnavailable,
    scheduleNotice,
    showsClassroomSchedule,
    showsYardSchedule,
    yardTrainingHours,
    unavailableStartDate
  } = useTrainingDateStep({
    locationExternalId,
    isTrainingStartDateDisabled,
    onTrainingStartDateChange,
    onYardDaysChange,
    trainingStartDate,
    trainingMode,
    yardDays
  })

  return (
    <div className="px-0 sm:px-4">
      <div className="mx-auto max-w-[72rem] animate-fade-in-up">
        <StepHeader currentStep={currentStep} totalSteps={totalSteps} onBack={onBack} />

        <div className="relative grid gap-6 px-4 pb-6 pt-4 lg:grid-cols-[minmax(0,1.25fr)_320px] lg:items-start">
          <div className="space-y-5">
            <div
              className="animate-fade-in-up overflow-hidden rounded-[28px] border border-[#f3e3d1] bg-white shadow-[0_20px_60px_rgba(102,72,34,0.08)]"
              style={{ animationDelay: '140ms' }}
            >
              <div className="border-b border-[#f7eadb] px-5 py-5 sm:px-7 sm:py-6">
                <p className="text-xs font-semibold uppercase tracking-[0.24em] text-primary">
                  Training Schedule
                </p>
                <h2 className="mt-2 text-[24px] font-semibold leading-tight text-gray-dark sm:text-[30px]">
                  Choose a start date that fits your week.
                </h2>
              </div>

              <div className="space-y-6 px-5 py-5 sm:px-7 sm:py-6">
                {!isYardOnly ? (
                  <TrainingStartDateSection
                    trainingStartDate={trainingStartDate}
                    onTrainingStartDateChange={onTrainingStartDateChange}
                    calendarMinDate={calendarMinDate}
                    calendarMaxDate={calendarMaxDate}
                    allowedDays={allowedDays}
                    availableStartDates={availableStartDates}
                    unavailableStartDate={unavailableStartDate}
                    disabled={isTrainingStartDateDisabled || isStartDateAvailabilityUnavailable}
                    scheduleNotice={scheduleNotice}
                  />
                ) : null}

                {showsYardSchedule && (
                  <YardDaySelector
                    yardDays={yardDays}
                    hasRequiredYardDays={hasRequiredYardDays}
                    disabled={areYardDaysDisabled}
                    onToggleYardDay={handleToggleYardDay}
                  />
                )}

                {isYardOnly ? (
                  <TrainingStartDateSection
                    trainingStartDate={trainingStartDate}
                    onTrainingStartDateChange={onTrainingStartDateChange}
                    calendarMinDate={calendarMinDate}
                    calendarMaxDate={calendarMaxDate}
                    allowedDays={allowedDays}
                    availableStartDates={availableStartDates}
                    unavailableStartDate={unavailableStartDate}
                    disabled={isTrainingStartDateDisabled || isStartDateAvailabilityUnavailable}
                    scheduleNotice={scheduleNotice}
                  />
                ) : null}

                <div className="flex w-full flex-col gap-3">
                  {errorMessage && <p className="text-sm text-red-600">{errorMessage}</p>}
                  {isStartDateAvailabilityError && showsClassroomSchedule && (
                    <p className="text-sm text-red-600">Unable to load available start dates.</p>
                  )}
                  <Button
                    type="button"
                    onClick={onContinue}
                    disabled={isContinueDisabled || isSubmitting}
                    fullWidth
                    className="hover:-translate-y-0.5 active:scale-95"
                  >
                    {isSubmitting ? 'Saving...' : 'Continue'}
                  </Button>
                </div>
              </div>
            </div>
          </div>

          <TrainingDateSidebar
            showsClassroomSchedule={showsClassroomSchedule}
            showsYardSchedule={showsYardSchedule}
            isClassroomOnly={isClassroomOnly}
            classroomSchedule={classroomSchedule}
            yardTrainingHours={yardTrainingHours}
          />
        </div>

        <div className="animate-fade-in-up pb-4 text-center" style={{ animationDelay: '320ms' }}>
          <EnrollmentHelpText />
        </div>
      </div>
    </div>
  )
}
